import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { Waitlist, type WaitlistTrackingProps } from '~/ui/waitlist';
import { BellIcon } from '~/ui/icons/bell';
import { TextLink } from '~/ui/link';

type AvailabilityEmptyStateProps = WaitlistTrackingProps & {
  /** When set, the empty state offers a "request a private event" link. */
  requestEventHref?: string;
};

export type { AvailabilityEmptyStateProps };

/**
 * Booking widget empty state — shown when an experience has no upcoming events.
 * The waitlist signup IS the affordance here, so it renders open with a
 * bell-icon avatar, heading and subtitle above the form and an optional
 * "request an event" link below. The heading/subtitle/link are owned here (not
 * by `Waitlist`) so the shared form stays chrome-free.
 */
export function AvailabilityEmptyState({
  requestEventHref,
  onViewed,
  onSubmitted,
}: AvailabilityEmptyStateProps) {
  return (
    <div className={panelStyles}>
      <Waitlist
        onViewed={onViewed}
        onSubmitted={onSubmitted}
        header={
          <Stack
            gap="xsmall"
            align="center"
            className={css({ textAlign: 'center', paddingBlock: 'medium' })}
          >
            <div className={panelAvatarStyles} aria-hidden="true">
              <BellIcon size="large-inset" />
            </div>
            <Text as="p" style="body-large-bold">
              <FormattedMessage
                defaultMessage="Get notified on new dates"
                description="Booking widget empty state heading"
              />
            </Text>
            <Text
              as="p"
              style="body-small-regular"
              className={css({ color: 'content.secondary' })}
            >
              <FormattedMessage
                defaultMessage="Add your email and we will let you know as soon as new dates are scheduled."
                description="Booking widget empty state subtitle"
              />
            </Text>
          </Stack>
        }
        footer={
          requestEventHref ? (
            <Text
              style="body-small-regular"
              className={css({ textAlign: 'center' })}
            >
              <FormattedMessage
                defaultMessage="Or <link>request an event</link>"
                description="Empty-state link offering visitors to request a private event"
                values={{
                  link: (chunks) => (
                    <TextLink to={requestEventHref} style="body-small-regular">
                      {chunks}
                    </TextLink>
                  ),
                }}
              />
            </Text>
          ) : undefined
        }
      />
    </div>
  );
}

const panelStyles = css({
  width: 'full',
});

const panelAvatarStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '[40px]',
  height: '[40px]',
  borderRadius: 'full',
  background: 'interactive.secondary',
  color: 'content.primary',
});
